<template>
  <div class="table-wrapper">
    <DataTableV01
      v-bind="$attrs"
      hide-default-footer
      :outlined="true"
      v-model="selected"
      :item-key="'dimension'"
      :items="data"
      :headers="tableHeaders"
      :show-select="multiSelect"
      :sort-by.sync="localSortKey"
      :sort-desc.sync="sortDesc"
      :custom-sort="customSort"
      :must-sort="true"
      :loading-text="'loading...'"
      @update:sort-by="emitSortData"
      @update:sort-desc="emitSortData"
    >
      <template v-slot:[`item.base`]="{ item }">
        <div :class="getDataColor(item.base)">
          {{ getCellDisplayValue(item.base, 'base') }}
        </div>
      </template>
      <template v-slot:[`item.comparison`]="{ item }">
        <div :class="getDataColor(item.comparison)">
          {{ getCellDisplayValue(item.comparison, 'comparison') }}
        </div>
      </template>
      <template v-slot:[`item.category`]="{ item }">
        <div :class="getDataColor(item.category)">
          {{ getCellDisplayValue(item.category, 'category') }}
        </div>
      </template>
      <template v-slot:[`item.subcategoryOne`]="{ item }">
        <div :class="getDataColor(item.subcategoryOne)">
          {{ getCellDisplayValue(item.subcategoryOne, 'subcategoryOne') }}
        </div>
      </template>
      <template v-slot:[`item.subcategoryTwo`]="{ item }">
        <div :class="getDataColor(item.subcategoryTwo)">
          {{ getCellDisplayValue(item.subcategoryTwo, 'subcategoryTwo') }}
        </div>
      </template>
      <template v-slot:[`item.subcategoryThree`]="{ item }">
        <div :class="getDataColor(item.subcategoryThree)">
          {{ getCellDisplayValue(item.subcategoryThree, 'subcategoryThree') }}
        </div>
      </template>
      <template v-slot:[`item.diff`]="{ item }">
        <div :class="getDiffColor(item.diff)">
          {{ getCellDisplayValue(item.diff, 'diff') }}
        </div>
      </template>
    </DataTableV01>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { DataTableV01 } from '@mikmak/components';

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    /** The value (not key) in columns array that you want to sort by when the table renders */
    sortKey: {
      type: String,
      required: false,
    },
    /** The default order direction. 1 for desc, -1 for asc. */
    sortOrder: {
      type: Number,
      required: false,
      default: -1,
    },
    isInPercentages: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    DataTableV01,
  },
  data() {
    return {
      localSortKey: '',
      sortDesc: true,
      selected: [],
    };
  },
  mounted() {
    if (this.multiSelect) this.selected = this.data;

    this.localSortKey = this.sortKey || this.columns[0].value;
    this.sortDesc = this.sortOrder === 1;
  },
  computed: {
    ...mapState({
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
    }),
    tableHeaders() {
      return this.columns.map((d, i) => {
        const headerObj = {
          align: i === 0 ? 'left' : 'center',
          text: d.name,
          value: d.value,
        };

        return headerObj;
      });
    },
    missingDataMessage() {
      return this.activeBenchmarksView === 'category'
        ? 'NON-SIG'
        : 'NO DATA';
    },
  },
  methods: {
    customSort(items, index, isDesc) {
      // force all null values to the bottom of any sort
      items.sort((a, b) => {
        a = a[index];
        b = b[index];
        if (a === b) return 0;
        else if (a === null) return 1;
        else if (b === null) return -1;
        else if (isDesc[0]) return b - a;
        else return a - b;
      });

      return items;
    },
    getCellDisplayValue(value, col) {
      if (value === null) {
        return col === 'diff' ? '-' : this.missingDataMessage;
      }

      let displayValue = value;

      switch (col) {
        case 'dimension':
          break;
        case 'diff':
          displayValue = `${value.toFixed(1)} pts`;
          if (value > 0) displayValue = '+' + displayValue;
          break;
        default:
          displayValue = value.toFixed(1);
          if (this.isInPercentages) displayValue += '%';
      }

      return displayValue;
    },
    getDataColor(value) {
      return value === null ? 'gray-benchmarks' : '';
    },
    getDiffColor(value) {
      if (!value) return '';

      if (value < 0) {
        return 'red-benchmarks';
      } else if (value > 0) {
        return 'green-benchmarks';
      } else {
        return 'gray-benchmarks';
      }
    },
    emitSortData() {
      this.$emit('sortData', {
        key: this.localSortKey,
        order: this.sortDesc ? 1 : -1,
      });
    },
  },
  watch: {
    selected(newVal) {
      this.$emit('updateCheckedData', newVal);
    },
    data(newVal) {
      if (this.multiSelect) this.selected = newVal;
    },
    sortKey(newVal) {
      this.localSortKey = newVal;
    },
    sortOrder(newVal) {
      this.sortDesc = newVal === 1;
    },
  },
};
</script>

<style scoped lang="scss">
.table-wrapper {
  min-height: 250px;
  overflow-y: scroll;
  position: relative;
  width: 100%;
}
.red-benchmarks {
  color: #CB4444;
}
.green-benchmarks {
  color: #00A6A4;
}
.gray-benchmarks {
  color: #778699;
}
</style>
